import { useEffect, useState, useMemo } from "react";
import { Util } from '../../util/Util';
import { canCreate, canDelete, canUpdate } from '../../util/access';
import ConfirmDialog from '../../components/ConfirmDialog';
import BomModal from './BomModal';
import BomReport from './BomReport';
import { BomProvider } from './BomContext';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { toast } from 'react-toastify';
import { Box, IconButton, ListItemIcon, MenuItem, Tooltip } from '@mui/material';
import {
	AddCircleOutline,
	CancelOutlined,
	CheckCircleOutline,
	Delete,
	DeleteOutline,
	Edit,
	Refresh,
	Visibility,
} from '@mui/icons-material';
import {
	NUMBER_FILTER_OPTIONS,
	TEXT_FILTER_OPTIONS,
} from '../../util/constants';
import TableView from '../../components/TableView';
import { MRT_ColumnDef } from 'material-react-table';

const BomRecordsInner = () => {
	const [data, setData] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
	const [toEdit, setToEdit] = useState<number | null>(null);
	const [toDelete, setToDelete] = useState<any>(null);
	const [toView, setToView] = useState<any>(null);
	const [newRecord, setNewRecord] = useState(false);

	const { api, access } = useAuth();
	const { setLoadingMessage, t } = useUI();

	const fetchData = async (useLoadModal = true) => {
		if (useLoadModal) setLoadingMessage('FETCH');
		setLoading(true);
		const res = await api.get('/prod-boms');
		if (res?.status === 200) {
			const list = Array.isArray(res.data)
				? res.data
				: res.data?.items || res.data?.data || [];
			setData(list);
			setLastUpdate(new Date());
		}
		setLoading(false);
		setLoadingMessage(null);
	};

	useEffect(() => {
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleDelete = async () => {
		if (!toDelete) return;
		setLoadingMessage('DELETE');
		const res = await api.delete(`/prod-boms/${toDelete.id}`);
		if (res?.status === 204) {
			toast.success(t('common:toast.success.delete'), { autoClose: 3000 });
			fetchData(false);
		} else if (res?.status === 409) {
			toast.error(t('common:toast.error.delete'), { autoClose: 3000 });
		}
		setLoadingMessage(null);
		setToDelete(null);
	};

	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t('common:production.bomName'),
			accessorKey: 'name',
			Cell: ({ cell }: { cell: any }) => (
				<button
					style={{
						background: 'none',
						border: 'none',
						padding: 0,
						color: 'inherit',
						cursor: 'pointer',
						textDecoration: 'underline',
					}}
					onClick={() => setToView(cell.row.original)}
				>
					{cell.getValue()}
				</button>
			),
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t('common:production.bomCode'),
			accessorKey: 'code',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t('common:production.productionDepot'),
			accessorKey: 'depot_name',
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t('common:production.inputTotal'),
			accessorKey: 'input_amt',
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue() || 0),
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t('common:production.outputTotal'),
			accessorKey: 'output_amt',
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue() || 0),
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t('common:production.inputs'),
			accessorKey: 'input_count',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t('common:production.outputs'),
			accessorKey: 'output_count',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t('common:status.active'),
			accessorKey: 'is_active',
			Cell: ({ cell }: { cell: any }) =>
				cell.getValue() ? (
					<CheckCircleOutline sx={{ color: 'green' }} />
				) : (
					<CancelOutlined sx={{ color: 'red' }} />
				),
		},
		{
			header: t('common:report.description'),
			accessorKey: 'description',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
	], [t]);

	return (
		<Box>
			<TableView
				resource="prod_bom"
				header={t('common:production.headers.boms')}
				data={data}
				columns={columns}
				lastUpdated={lastUpdate}
				isLoading={loading}
				useDateRangeFilter={false}
				totals={[
					{ key: 'input_amt', label: t('common:production.inputTotal') },
					{ key: 'output_amt', label: t('common:production.outputTotal') },
				]}
				renderTopToolbarActions={() => (
					<Box sx={{ display: 'flex', gap: '0.5rem' }}>
						<Tooltip title={t('common:ui.tooltips.refreshData')}>
							<IconButton onClick={() => fetchData()} color="primary">
								<Refresh />
							</IconButton>
						</Tooltip>
						<Tooltip title={t('common:ui.tooltips.newBom')}>
							<IconButton
								onClick={() => {
									// A bill of materials is a table of its own now; writing
									// one needs reading it, as every resource write does.
									if (canCreate(access, 'prod_bom')) {
										setNewRecord(true);
									}
								}}
								color="success"
							>
								<AddCircleOutline />
							</IconButton>
						</Tooltip>
					</Box>
				)}
				renderRowActionMenuItems={(row, closeMenu) => [
					<MenuItem
						key="view"
						onClick={() => {
							setToView(row.original);
							closeMenu();
						}}
						sx={{ color: 'info.main' }}
					>
						<ListItemIcon sx={{ color: 'info.main' }}>
							<Visibility />
						</ListItemIcon>
						{t('common:ui.buttons.view')}
					</MenuItem>,
					<MenuItem
						key="edit"
						onClick={() => {
							if (canUpdate(access, 'prod_bom')) {
								setToEdit(row.original.id);
							}
							closeMenu();
						}}
						sx={{ color: 'primary.main' }}
					>
						<ListItemIcon sx={{ color: 'primary.main' }}>
							<Edit />
						</ListItemIcon>
						{t('common:ui.buttons.edit')}
					</MenuItem>,
					<MenuItem
						key="delete"
						onClick={() => {
							if (canDelete(access, 'prod_bom')) {
								setToDelete(row.original);
							}
							closeMenu();
						}}
						sx={{ color: 'error.main' }}
					>
						<ListItemIcon sx={{ color: 'error.main' }}>
							<Delete />
						</ListItemIcon>
						{t('common:ui.buttons.delete')}
					</MenuItem>,
				]}
			/>

			{/* Keep mounted while open/closing so onTransitionExited can refresh the table. */}
			<BomModal
				isOpen={newRecord || !!toEdit}
				toggle={() => {
					setNewRecord(false);
					setToEdit(null);
				}}
				bomId={toEdit}
				onSuccess={() => fetchData(false)}
			/>

			{toView && (
				<BomReport
					isOpen={!!toView}
					toggle={() => setToView(null)}
					bundle={toView}
				/>
			)}

			<ConfirmDialog
				onConfirm={handleDelete}
				onCancel={() => setToDelete(null)}
				isOpen={!!toDelete}
				setIsOpen={() => {}}
				title={
					<>
						<DeleteOutline sx={{ color: 'red' }} />{' '}
						{t('common:dialog.deleteConfirmation')}
					</>
				}
				message={t('common:production.deleteBom')}
				confirmBtnColor="error"
				confirmBtnVariant="contained"
				confirmBtnText={<>{t('common:ui.buttons.delete')}</>}
			/>
		</Box>
	);
};

/** Provider wraps modal multi-task state for BOM editing. */
const BomRecords = () => (
	<BomProvider>
		<BomRecordsInner />
	</BomProvider>
);

export default BomRecords;
